import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthContext } from '@/contexts/AuthContext'
import { usePaywallContext } from '@/contexts/PaywallContext'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ref, get } from 'firebase/database'
import { database } from '@/services/firebase'

interface ScoringAccessGuardProps {
  children: React.ReactNode
}

export const ScoringAccessGuard: React.FC<ScoringAccessGuardProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuthContext()
  const { isSubscriptionActive, isLoading: paywallLoading } = usePaywallContext()
  const [isValidating, setIsValidating] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (loading || paywallLoading) return

    const validateAccess = async () => {
      // FIRST: Check for controller token or display-only access (regardless of auth status)
      const searchParams = new URLSearchParams(location.search)
      const matchId = searchParams.get('matchId')
      const token = searchParams.get('token')

      // If there's a matchId but no token, this is a display-only request
      if (matchId && !token) {
        // Allow display-only access for anyone
        setHasAccess(true)
        setIsValidating(false)
        return
      }

      // If there's both matchId and token, validate controller access
      if (matchId && token) {
        try {
          const controllerRef = ref(database, `matches/${matchId}/controllers/${token}`)
          const controllerSnapshot = await get(controllerRef)

          if (controllerSnapshot.exists()) {
            const controller = controllerSnapshot.val()
            const now = Date.now()

            // Check if token is expired
            if (controller.expiresAt < now) {
              setHasAccess(false)
              setIsValidating(false)
              navigate('/auth', { replace: true })
              return
            }

            // Check if token is active
            if (controller.isActive) {
              setHasAccess(true)
              setIsValidating(false)
              return
            }
          }
        } catch (error) {
          console.error('Error validating controller token:', error)
        }
      }

      // SECOND: Check for authenticated user with active subscription
      if (isAuthenticated && isSubscriptionActive) {
        setHasAccess(true)
        setIsValidating(false)
        return
      }
      
      // THIRD: If user is authenticated but subscription is not active and no valid token, redirect to paywall
      if (isAuthenticated && !isSubscriptionActive) {
        setHasAccess(false)
        setIsValidating(false)
        navigate('/paywall', { replace: true })
        return
      }

      // No valid access found
      setHasAccess(false)
      setIsValidating(false)
      navigate('/auth', { replace: true })
    }

    validateAccess()
  }, [isAuthenticated, isSubscriptionActive, loading, paywallLoading, location.search, navigate])

  if (isValidating || loading || paywallLoading) {
    return <LoadingSpinner />
  }

  if (hasAccess) {
    return <>{children}</>
  }

  return null
} 